// src/pages/Kostenvergleich.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const euro = (value) =>
  new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 0,
  }).format(Math.round(value));

function MobileSystemCostCards({ systems, bestKey }) {
  return (
    <div className="md:hidden space-y-4 mt-6">
      {systems.map((s) => (
        <div
          key={s.label}
          className={`rounded-2xl border p-5 shadow-sm ${
            s.key === bestKey
              ? "border-emerald-300 bg-emerald-50/70"
              : "border-slate-200 bg-white"
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <h4 className="text-lg font-semibold text-slate-900">{s.label}</h4>
            {s.key === bestKey && (
              <span className="shrink-0 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                günstigste
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            <span className="text-slate-500">Investition</span>
            <span className="text-right text-slate-800">{euro(s.invest)} €</span>

            <span className="text-slate-500">Betrieb</span>
            <span className="text-right text-slate-800">{euro(s.operating)} €</span>

            <span className="text-slate-500">Wartung</span>
            <span className="text-right text-slate-800">{euro(s.maintenance)} €</span>

            {s.subsidy > 0 && (
              <>
                <span className="text-slate-500">Förderung</span>
                <span className="text-right font-medium text-emerald-700">
                  − {euro(s.subsidy)} €
                </span>
              </>
            )}

            <span className="mt-2 border-t border-slate-200 pt-3 font-medium text-slate-700">
              Gesamtkosten
            </span>
            <span className="mt-2 border-t border-slate-200 pt-3 text-right text-base font-bold text-slate-950">
              {euro(s.total)} €
            </span>

            <span className="text-slate-500">JAZ / Wirkungsgrad</span>
            <span className="text-right text-slate-800">{s.efficiencyLabel}</span>

            <span className="text-slate-500">Amortisation ggü. Gas</span>
            <span className="text-right text-slate-800">{s.paybackLabel}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function InfoBox({ title, children, color = "gray" }) {
  const styles = {
    gray: "bg-slate-50 border-slate-200",
    green: "bg-emerald-50/70 border-emerald-200",
    blue: "bg-sky-50/70 border-sky-200",
    red: "bg-rose-50/70 border-rose-200",
    amber: "bg-amber-50/70 border-amber-200",
  };

  return (
    <div className={`h-full rounded-2xl border p-6 ${styles[color]}`}>
      <h3 className="mb-3 text-lg font-semibold text-slate-950">{title}</h3>
      <div className="space-y-3 text-sm leading-6 text-slate-700 sm:text-base">
        {children}
      </div>
    </div>
  );
}

export default function Kostenvergleich() {
  const [scenario, setScenario] = useState("sanierung");
  const [soilQuality, setSoilQuality] = useState("mittel");
  const [foerderung, setFoerderung] = useState(true);
  const [selfOccupied, setSelfOccupied] = useState(true);
  const [speedBonus, setSpeedBonus] = useState(true);
  const [taxableHouseholdIncome, setTaxableHouseholdIncome] = useState(70000);
  const [hasEligibleChild, setHasEligibleChild] = useState(false);

  const [heatDemand, setHeatDemand] = useState(15000);
  const [strompreis, setStrompreis] = useState(0.3);
  const [gaspreis, setGaspreis] = useState(0.11);
  const [co2Price, setCo2Price] = useState(60);
  const [years, setYears] = useState(20);

  useEffect(() => {
	document.title =
	"Erdwärmepumpe: Kosten, Förderung & Vergleich | Erdwärme-Experten";

    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
	meta.setAttribute(
	"content",
	"Was kostet eine Erdwärmepumpe? Interaktiver Kostenrechner für Erdsonde, Erdkollektor, Förderung, Betriebskosten, JAZ und Amortisation im Vergleich zu Gas und Luft-Wärmepumpe."
	);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://www.erdwaerme-experten.de/erdwaermepumpe-kosten";
  }, []);

  const scenarioData = {
    neubau: {
      label: "Neubau",
      note:
        "Im Neubau ist die pauschale Heizungstausch-Förderung nicht mit dem Bestand vergleichbar. Die Förderung wird hier daher bewusst zurückhaltend modelliert.",
      gasInvest: 12000,
      luftInvest: 22000,
      kollektorInvest: 24200,
      sondeInvest: 29000,
      luftJaz: 3.4,
      kollektorJaz: 4.1,
      sondeJaz: 4.6,
    },
    sanierung: {
      label: "Sanierung / Heizungstausch",
      note:
        "Für Bestandsgebäude kann die BEG-Heizungsförderung die Netto-Investition deutlich reduzieren. Entscheidend sind Eigentümerstatus, Altanlage, Einkommen und technische Anforderungen.",
      gasInvest: 15000,
      luftInvest: 26000,
      kollektorInvest: 27000,
      sondeInvest: 33000,
      luftJaz: 3.2,
      kollektorJaz: 4.0,
      sondeJaz: 4.5,
    },
  };

  const soilFactors = {
    schlecht: {
      label: "Geringe Bodenqualität",
      short: "gering",
      investFactor: 1.15,
      jazDeltaKollektor: -0.25,
      jazDeltaSonde: -0.2,
      description:
        "Bei ungünstiger Wärmeleitfähigkeit oder trockenen Böden können größere Kollektorflächen oder längere Sonden erforderlich werden.",
    },
    mittel: {
      label: "Mittlere Bodenqualität",
      short: "mittel",
      investFactor: 1.0,
      jazDeltaKollektor: 0,
      jazDeltaSonde: 0,
      description:
        "Mittlere Bodenverhältnisse entsprechen einer typischen überschlägigen Vergleichsrechnung.",
    },
    gut: {
      label: "Gute Bodenqualität",
      short: "gut",
      investFactor: 0.9,
      jazDeltaKollektor: 0.2,
      jazDeltaSonde: 0.2,
      description:
        "Gute Wärmeleitfähigkeit kann Erschließungskosten senken und die Jahresarbeitszahl leicht verbessern.",
    },
  };

  const d = scenarioData[scenario];
  const soil = soilFactors[soilQuality];

  const MAX_FOERDERSATZ = 0.8;
  const MAX_FOERDERFAEHIGE_KOSTEN = 28000;
  const GAS_EFFICIENCY = 0.95;
  const GAS_CO2_KG_PER_KWH = 0.201; // überschlägiger Emissionsfaktor Erdgas

  const calculateIncomeBonus = () => {
    if (!selfOccupied) return 0;

    // Seit 21.07.2026 erhöhen sich die Einkommensgrenzen einmalig um
    // 10.000 €, wenn mindestens ein kindergeldberechtigtes Kind unter 18
    // Jahren im Haushalt lebt.
    const familyAllowance = hasEligibleChild ? 10000 : 0;

    if (taxableHouseholdIncome <= 30000 + familyAllowance) return 0.4;
    if (taxableHouseholdIncome <= 40000 + familyAllowance) return 0.3;
    if (taxableHouseholdIncome <= 50000 + familyAllowance) return 0.1;
    return 0;
  };

  const calculateSubsidy = ({ type, invest }) => {
    if (!foerderung || scenario !== "sanierung") return { rate: 0, amount: 0 };

    let rate = 0;

    if (type === "luft" || type === "kollektor" || type === "sonde") {
      rate += 0.3; // Grundförderung
    }

    if (selfOccupied && speedBonus && type !== "gas") {
      rate += 0.16; // Klimageschwindigkeitsbonus seit 21.07.2026
    }

    if (type !== "gas") {
      rate += calculateIncomeBonus();
    }

    rate = Math.min(rate, MAX_FOERDERSATZ);

    const eligibleCosts = Math.min(invest, MAX_FOERDERFAEHIGE_KOSTEN);
    return {
      rate,
      amount: eligibleCosts * rate,
    };
  };

  const systemsRaw = [
    {
      key: "gas",
      label: "Gas-Heizung",
      type: "gas",
      invest: d.gasInvest,
      efficiency: GAS_EFFICIENCY,
      efficiencyLabel: "95 %",
      maintenancePerYear: 450,
      description:
        "Geringere Anfangsinvestition, aber abhängig von Gaspreis, CO₂-Kosten und fossiler Preisentwicklung.",
    },
    {
      key: "luft",
      label: "Luft-Wärmepumpe",
      type: "luft",
      invest: d.luftInvest,
      jaz: d.luftJaz,
      maintenancePerYear: 250,
      description:
        "Meist geringere Investition als Erdwärme, dafür stärker schwankende Effizienz bei kalten Außentemperaturen.",
    },
    {
      key: "kollektor",
      label: "Erdkollektoren mit Wärmepumpe",
      type: "kollektor",
      invest: d.kollektorInvest * soil.investFactor,
      jaz: Math.max(3.4, d.kollektorJaz + soil.jazDeltaKollektor),
      maintenancePerYear: 230,
      description:
        "Günstiger als Erdsonden, aber großer Flächenbedarf und stärkere Abhängigkeit von Bodenfeuchte und Nutzung der Gartenfläche.",
    },
    {
      key: "sonde",
      label: "Erdsonden mit Wärmepumpe",
      type: "sonde",
      invest: d.sondeInvest * soil.investFactor,
      jaz: Math.max(3.6, d.sondeJaz + soil.jazDeltaSonde),
      maintenancePerYear: 230,
      description:
        "Höhere Erschließungskosten, aber sehr stabile Quelle und meist die beste Jahresarbeitszahl.",
    },
  ];

  const systems = systemsRaw.map((s) => {
    let operating = 0;
    let yearlyCost = 0;
    let yearlyEnergy = 0;
    let yearlyCo2Cost = 0;

    if (s.type === "gas") {
      const gasKwh = heatDemand / GAS_EFFICIENCY;
      yearlyEnergy = gasKwh;
      yearlyCo2Cost = (gasKwh * GAS_CO2_KG_PER_KWH * co2Price) / 1000;
      yearlyCost = gasKwh * gaspreis + yearlyCo2Cost;
      operating = yearlyCost * years;
    } else {
      const electricityKwh = heatDemand / s.jaz;
      yearlyEnergy = electricityKwh;
      yearlyCost = electricityKwh * strompreis;
      operating = yearlyCost * years;
    }

    const maintenance = s.maintenancePerYear * years;
    const subsidy = calculateSubsidy({ type: s.type, invest: s.invest });
    const total = s.invest + operating + maintenance - subsidy.amount;

    return {
      ...s,
      yearlyEnergy,
      yearlyCost,
      yearlyCo2Cost,
      operating,
      maintenance,
      subsidy: subsidy.amount,
      subsidyRate: subsidy.rate,
      total,
      efficiencyLabel:
        s.type === "gas" ? s.efficiencyLabel : `JAZ ${s.jaz.toFixed(1)}`,
    };
  });

  const gasSystem = systems.find((s) => s.key === "gas");

  const systemsWithPayback = systems.map((s) => {
    if (s.key === "gas") {
      return {
        ...s,
        paybackLabel: "Referenz",
        annualSavingVsGas: 0,
      };
    }

    const extraInvestment =
      s.invest - s.subsidy - (gasSystem.invest - gasSystem.subsidy);
    const annualSaving =
      gasSystem.yearlyCost +
      gasSystem.maintenancePerYear -
      (s.yearlyCost + s.maintenancePerYear);

    let paybackLabel = "nicht wirtschaftlich";

    if (annualSaving > 0 && extraInvestment > 0) {
      paybackLabel = `${(extraInvestment / annualSaving).toFixed(1)} Jahre`;
    }

    if (extraInvestment <= 0 && annualSaving > 0) {
      paybackLabel = "sofort günstiger";
    }

    return {
      ...s,
      paybackLabel,
      annualSavingVsGas: annualSaving,
    };
  });

  const dataKosten = {
    labels: systemsWithPayback.map((s) => s.label),
    datasets: [
      {
        label: "Investition",
        data: systemsWithPayback.map((s) => Math.round(s.invest)),
        backgroundColor: "#ffb3b3",
      },
      {
        label: `Betriebskosten (${years} Jahre)`,
        data: systemsWithPayback.map((s) => Math.round(s.operating)),
        backgroundColor: "#ffe29a",
      },
      {
        label: `Wartung (${years} Jahre)`,
        data: systemsWithPayback.map((s) => Math.round(s.maintenance)),
        backgroundColor: "#d9d9d9",
      },
      {
        label: "Förderung",
        data: systemsWithPayback.map((s) => -Math.round(s.subsidy)),
        backgroundColor: "#a8e6cf",
      },
    ],
  };

  const optionsKosten = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 20,
          color: "#475569",
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${euro(ctx.parsed.y)} €`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          color: "#64748b",
          callback: (v) => `${euro(v)} €`,
        },
        grid: {
          color: "#e2e8f0",
        },
      },
      x: {
        ticks: {
          color: "#475569",
          maxRotation: 0,
          autoSkip: false,
          callback(value) {
            const label = this.getLabelForValue(value);
            return label
              .replace(" mit Wärmepumpe", "")
              .replace("Gas-Heizung", "Gas")
              .replace("Luft-Wärmepumpe", "Luft-WP");
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const bestSystem = systemsWithPayback.reduce((best, current) =>
    current.total < best.total ? current : best
  );

  const savingsVsGas = Math.max(0, gasSystem.total - bestSystem.total);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-[#f6f3f2] pb-20 pt-14 sm:pt-20">
      {/* HERO */}
      <div className="mx-auto mb-12 max-w-6xl px-4 sm:px-6">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-red-600 sm:text-sm">
          Wirtschaftlichkeit & Förderung
        </p>

			<h1 className="max-w-5xl text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl lg:leading-[1.12]">
			  Erdwärmepumpe Kosten: Erdsonde, Erdkollektor, Förderung und Amortisation
			</h1>

        <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
          Vergleichen Sie typische Gesamtkosten über mehrere Jahre: Investition,
          Förderung, Strom- und Gaskosten, CO₂-Kosten, Wartung und Amortisation.
          So wird sichtbar, wann Erdwärme trotz höherer Anfangsinvestition
          wirtschaftlich interessant sein kann.
        </p>

        <div className="mt-6 flex flex-wrap gap-2.5 text-sm text-slate-600">
          <span className="rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
            4 Heizsysteme im Vergleich
          </span>
          <span className="rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
            Förderung Stand 21.07.2026
          </span>
          <span className="rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
            Frei anpassbare Annahmen
          </span>
        </div>
      </div>

      {/* INTERAKTIVER RECHNER */}
      <div className="mx-4 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/40 sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <div className="mb-8 flex flex-col gap-6 border-b border-slate-200 pb-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="mb-2 text-sm font-semibold text-emerald-700">
              Interaktiver Rechner
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
              Interaktiver Heizkostenvergleich
            </h2>

            <p className="mt-3 max-w-3xl leading-7 text-slate-600">
              Die Berechnung nutzt typische Kostenbereiche für ein
              Einfamilienhaus und kombiniert diese mit frei anpassbaren
              Annahmen zu Wärmebedarf, Energiepreisen, CO₂-Preis, Förderung und
              Bodenqualität. Die Werte sind bewusst als Orientierung gedacht und
              ersetzen keine Fachplanung.
            </p>
          </div>

          <div className="min-w-[270px] rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 p-6 text-white shadow-lg shadow-emerald-900/10">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-100">
              Günstigste Variante im Beispiel
            </p>
            <p className="mt-2 text-xl font-bold leading-tight">
              {bestSystem.label}
            </p>
            <p className="mt-3 text-2xl font-bold">
              {euro(bestSystem.total)} €
            </p>
            <p className="mt-1 text-sm text-emerald-100">
              Gesamtkosten über {years} Jahre
            </p>
          </div>
        </div>

        {/* Eingaben */}
        <div className="mb-8 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-950">Grundannahmen</p>
                <p className="mt-1 text-xs text-slate-500">
                  Änderungen werden sofort im Ergebnis berücksichtigt
                </p>
              </div>
              <span className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-500 shadow-sm ring-1 ring-slate-200">
                Schritt 1
              </span>
            </div>

            <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
          <label className="flex flex-col gap-2 text-sm text-gray-700">
            Situation
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
            >
              <option value="sanierung">Sanierung / Heizungstausch</option>
              <option value="neubau">Neubau</option>
            </select>
          </label>

          <label className="flex flex-col gap-2 text-sm text-gray-700">
            Bodenqualität
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={soilQuality}
              onChange={(e) => setSoilQuality(e.target.value)}
            >
              <option value="schlecht">gering</option>
              <option value="mittel">mittel</option>
              <option value="gut">gut</option>
            </select>
          </label>

          <label className="flex flex-col gap-2 text-sm text-gray-700">
            Jahreswärmebedarf
            <input
              type="number"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={heatDemand}
              min="5000"
              step="1000"
              onChange={(e) => setHeatDemand(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">kWh Wärme pro Jahr</span>
          </label>

          <label className="flex flex-col gap-2 text-sm text-gray-700">
            Betrachtungszeitraum
            <input
              type="number"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={years}
              min="5"
              max="40"
              step="5"
              onChange={(e) => setYears(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">Jahre</span>
          </label>

          <label className="flex flex-col gap-2 text-sm text-gray-700">
            Strompreis
            <input
              type="number"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={strompreis}
              min="0.1"
              max="0.7"
              step="0.01"
              onChange={(e) => setStrompreis(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">€/kWh</span>
          </label>

          <label className="flex flex-col gap-2 text-sm text-gray-700">
            Gaspreis
            <input
              type="number"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={gaspreis}
              min="0.04"
              max="0.3"
              step="0.01"
              onChange={(e) => setGaspreis(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">€/kWh</span>
          </label>

          <label className="flex flex-col gap-2 text-sm text-gray-700">
            CO₂-Preis
            <input
              type="number"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              value={co2Price}
              min="0"
              max="250"
              step="5"
              onChange={(e) => setCo2Price(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">€/t CO₂</span>
          </label>

            </div>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-950">Förderprofil</p>
                <p className="mt-1 text-xs text-slate-500">
                  BEG-Heizungsförderung 2026
                </p>
              </div>
              <span className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 shadow-sm ring-1 ring-emerald-200">
                Schritt 2
              </span>
            </div>

            <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-xl bg-white p-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-600"
                checked={foerderung}
                onChange={() => setFoerderung(!foerderung)}
              />
              <span className="font-medium">Förderung berücksichtigen</span>
            </label>

            <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-xl bg-white p-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-600"
                checked={selfOccupied}
                onChange={() => setSelfOccupied(!selfOccupied)}
                disabled={!foerderung || scenario !== "sanierung"}
              />
              Selbstgenutzter Hauptwohnsitz
            </label>

            <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-xl bg-white p-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-600"
                checked={speedBonus}
                onChange={() => setSpeedBonus(!speedBonus)}
                disabled={
                  !foerderung || scenario !== "sanierung" || !selfOccupied
                }
              />
              <span>
                Klimageschwindigkeitsbonus
                <strong className="ml-1 text-emerald-700">+16 %</strong>
              </span>
            </label>

            <label className="mt-4 flex flex-col gap-2 text-sm font-medium text-slate-700">
              Zu versteuerndes Haushaltsjahreseinkommen
              <input
                type="number"
                className="w-full rounded-xl border border-emerald-200 bg-white px-3.5 py-3 font-normal text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-400"
                value={taxableHouseholdIncome}
                min="0"
                step="1000"
                onChange={(e) =>
                  setTaxableHouseholdIncome(Number(e.target.value))
                }
                disabled={
                  !foerderung || scenario !== "sanierung" || !selfOccupied
                }
              />
              <span className="text-xs font-normal text-slate-500">Euro (zVE)</span>
            </label>

            <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm leading-5 text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600"
                checked={hasEligibleChild}
                onChange={() => setHasEligibleChild(!hasEligibleChild)}
                disabled={
                  !foerderung || scenario !== "sanierung" || !selfOccupied
                }
              />
              Kindergeldberechtigtes Kind unter 18 im Haushalt
            </label>

            <div className="mt-5 flex items-center justify-between rounded-xl bg-emerald-700 px-4 py-3 text-white">
              <span className="text-sm">Einkommensbonus</span>
              <strong className="text-lg">
                {foerderung && scenario === "sanierung" && selfOccupied
                  ? `${calculateIncomeBonus() * 100} %`
                  : "0 %"}
              </strong>
            </div>
          </div>
        </div>

        <div className="mb-8 rounded-2xl border border-sky-200 bg-sky-50/70 p-5">
          <p className="text-sm leading-6 text-slate-700">
            <b className="text-slate-950">Förderstand 21.07.2026:</b> {d.note}{" "}
            Berücksichtigt werden
            30&nbsp;% Grundförderung sowie bei erfüllten Voraussetzungen
            16&nbsp;% Klimageschwindigkeitsbonus und 10, 30 oder 40&nbsp;%
            Einkommensbonus. Der frühere Effizienzbonus für Wärmepumpen ist
            entfallen. Die Förderung ist auf 80&nbsp;% beziehungsweise maximal
            22.400&nbsp;€ bei 28.000&nbsp;€ förderfähigen Kosten für die erste
            Wohneinheit begrenzt.
          </p>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-500">Günstigstes System</p>
            <p className="mt-2 font-semibold text-slate-950">{bestSystem.label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-950">
              {euro(bestSystem.total)} €
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-500">Vorteil gegenüber Gas</p>
            <p className="mt-2 text-2xl font-bold text-emerald-700">
              {euro(savingsVsGas)} €
            </p>
            <p className="mt-1 text-sm text-slate-500">über {years} Jahre</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-500">Förderung der Variante</p>
            <p className="mt-2 text-2xl font-bold text-emerald-700">
              {euro(bestSystem.subsidy)} €
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {Math.round(bestSystem.subsidyRate * 100)} % Fördersatz
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-6">
          <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-950">
                Kostenaufteilung im Vergleich
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Investition, Betrieb, Wartung und Förderung über {years} Jahre
              </p>
            </div>
            <span className="text-xs text-slate-500">
              Bodenqualität: {soil.short}
            </span>
          </div>

          <div className="hidden h-[390px] md:block">
            <Bar data={dataKosten} options={optionsKosten} />
          </div>

          <MobileSystemCostCards
            systems={systemsWithPayback}
            bestKey={bestSystem.key}
          />
        </div>

        <p className="mt-5 text-sm leading-6 text-slate-500">
          <b>Hinweis:</b> Die dargestellten Werte sind überschlägige
          Vergleichswerte. Tatsächliche Kosten hängen von Gebäudezustand,
          Heizlast, Vorlauftemperatur, Bohrtiefe, Bodenaufbau, Genehmigung,
          Region, Angeboten, Förderung und Energiepreisen ab.
        </p>

        {/* Ergebnistabelle */}
        <h3 className="mb-4 mt-10 text-xl font-semibold text-slate-950">
          Gesamtkosten über {years} Jahre – Bodenqualität:{" "}
          <span className="text-gray-700 font-medium">{soil.short}</span>
        </h3>

        <div className="hidden overflow-hidden rounded-2xl border border-slate-200 md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="px-4 py-3.5 text-left font-semibold">System</th>
                <th className="px-4 py-3.5 text-right font-semibold">Investition</th>
                <th className="px-4 py-3.5 text-right font-semibold">Förderung</th>
                <th className="px-4 py-3.5 text-right font-semibold">pro Jahr</th>
                <th className="px-4 py-3.5 text-right font-semibold">Gesamt</th>
                <th className="px-4 py-3.5 text-right font-semibold">Amortisation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {systemsWithPayback.map((s) => (
                <tr
                  key={s.key}
                  className={
                    s.key === bestSystem.key ? "bg-emerald-50/70" : "bg-white"
                  }
                >
                  <td className="px-4 py-4 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      {s.label}
                      {s.key === bestSystem.key && (
                        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          günstigste
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right text-slate-700">
                    {euro(s.invest)} €
                  </td>
                  <td className="px-4 py-4 text-right font-medium text-emerald-700">
                    {s.subsidy > 0
                      ? `− ${euro(s.subsidy)} € (${Math.round(
                          s.subsidyRate * 100
                        )} %)`
                      : "–"}
                  </td>
                  <td className="px-4 py-4 text-right text-slate-700">
                    {euro(s.yearlyCost + s.maintenancePerYear)} €/a
                  </td>
                  <td className="px-4 py-4 text-right font-bold text-slate-950">
                    {euro(s.total)} €
                  </td>
                  <td className="px-4 py-4 text-right text-slate-700">
                    {s.paybackLabel}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-sm leading-6 text-slate-600">
          {soil.description} Besonders bei Erdsonden beeinflussen
          Wärmeleitfähigkeit, Grundwasserverhältnisse und zulässige Bohrtiefe
          die benötigte Sondenlänge und damit die Investitionskosten.
        </p>
      </div>

      {/* ERKLÄRUNG: WARUM ERDWÄRME ANDERS IST */}
      <section className="mx-4 mt-10 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <p className="text-sm font-semibold text-emerald-700">
          Langfristige Betrachtung
        </p>
        <h2 className="mb-6 mt-2 max-w-3xl text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Warum Erdwärme trotz höherer Anfangskosten interessant sein kann
        </h2>

        <div className="grid gap-5 md:grid-cols-2">
          <InfoBox title="Stabile Quelle statt kalter Außenluft" color="green">
            <p>
              Erdgekoppelte Wärmepumpen nutzen das Erdreich als Wärmequelle.
              Dadurch schwankt die Quellentemperatur im Winter deutlich weniger
              als bei einer Luft-Wasser-Wärmepumpe.
            </p>
            <p>
              Das führt häufig zu höheren Jahresarbeitszahlen. Eine JAZ von 4,5
              bedeutet: Aus 1&nbsp;kWh Strom entstehen über das Jahr betrachtet
              etwa 4,5&nbsp;kWh Wärme.
            </p>
          </InfoBox>

          <InfoBox title="Investition und Betrieb getrennt betrachten" color="blue">
            <p>
              Gasheizungen wirken bei der Anschaffung oft günstiger. Über viele
              Jahre können jedoch Brennstoffkosten, CO₂-Kosten und Wartung den
              anfänglichen Vorteil aufzehren.
            </p>
            <p>
              Erdwärme hat dagegen meist höhere Erschließungskosten, aber
              geringere laufende Kosten und eine sehr langlebige
              Wärmequellenanlage.
            </p>
          </InfoBox>
        </div>
      </section>

      {/* ERDSONDE VS KOLLEKTOR */}
      <section className="mx-4 mt-10 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <p className="text-sm font-semibold text-red-600">Systemvergleich</p>
        <h2 className="mb-3 mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Erdsonde oder Flächenkollektor: Was ist wirtschaftlicher?
        </h2>
        <p className="mb-7 max-w-3xl leading-7 text-slate-600">
          Beide Varianten nutzen das Erdreich, unterscheiden sich aber deutlich
          bei Fläche, Erschließung und Genehmigung.
        </p>

        <p className="mb-3 text-xs font-medium text-slate-500 sm:hidden">
          Tabelle horizontal wischen, um alle Spalten zu sehen.
        </p>

        <div
          className="overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-200"
          role="region"
          aria-label="Vergleich von Erdsonde und Flächenkollektor"
          tabIndex={0}
        >
          <table className="w-full min-w-[680px] text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="sticky left-0 z-10 border bg-slate-900 p-3 text-left">Kriterium</th>
                <th className="p-3 border text-left">Erdsonde</th>
                <th className="p-3 border text-left">Flächenkollektor</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="sticky left-0 z-10 border bg-white p-3 font-medium">Typische JAZ</td>
                <td className="p-3 border">ca. 4,3 bis 5,0</td>
                <td className="p-3 border">ca. 3,8 bis 4,3</td>
              </tr>
              <tr>
                <td className="sticky left-0 z-10 border bg-white p-3 font-medium">Erschließungskosten</td>
                <td className="p-3 border">höher durch Bohrung</td>
                <td className="p-3 border">meist günstiger</td>
              </tr>
              <tr>
                <td className="sticky left-0 z-10 border bg-white p-3 font-medium">Flächenbedarf</td>
                <td className="p-3 border">gering, nur Bohrpunkte</td>
                <td className="p-3 border">hoch, oft mehrere hundert m²</td>
              </tr>
              <tr>
                <td className="sticky left-0 z-10 border bg-white p-3 font-medium">Genehmigung</td>
                <td className="p-3 border">
                  wasserrechtlich relevant, regional unterschiedlich
                </td>
                <td className="p-3 border">
                  meist einfacher, aber ebenfalls standortabhängig
                </td>
              </tr>
              <tr>
                <td className="sticky left-0 z-10 border bg-white p-3 font-medium">Nutzung des Gartens</td>
                <td className="p-3 border">kaum eingeschränkt</td>
                <td className="p-3 border">
                  Fläche sollte nicht überbaut oder versiegelt werden
                </td>
              </tr>
              <tr>
                <td className="sticky left-0 z-10 border bg-white p-3 font-medium">Geeignet für</td>
                <td className="p-3 border">
                  kleine Grundstücke, hohe Effizienz, langfristige Lösung
                </td>
                <td className="p-3 border">
                  große Grundstücke, niedrigere Anfangskosten
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-6 rounded-2xl bg-slate-50 p-5 leading-7 text-slate-700">
          Vereinfacht gesagt: Flächenkollektoren sind oft günstiger, benötigen
          aber viel freie Gartenfläche. Erdsonden sind teurer und stärker
          genehmigungsabhängig, liefern dafür aber meist die stabilere und
          effizientere Wärmequelle.
        </p>
      </section>

      {/* FÖRDERUNG */}
      <section className="mx-4 mt-10 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <p className="text-sm font-semibold text-emerald-700">Förderung 2026</p>
        <h2 className="mb-6 mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Förderung: Warum Erdwärme im Bestand besonders profitieren kann
        </h2>

        <div className="grid gap-5 md:grid-cols-3">
          <InfoBox title="Grundförderung" color="green">
            <p className="text-3xl font-bold text-emerald-700">30 %</p>
            <p>
              Für den Einbau einer förderfähigen Wärmepumpe in einem mindestens
              fünf Jahre alten Bestandsgebäude werden 30&nbsp;% Grundförderung
              angesetzt. Der frühere Effizienzbonus für bestimmte Wärmequellen
              gilt für Anträge seit dem 21.07.2026 nicht mehr.
            </p>
          </InfoBox>

          <InfoBox title="Klimageschwindigkeitsbonus" color="blue">
            <p className="text-3xl font-bold text-sky-700">+16 %</p>
            <p>
              Der Klimageschwindigkeitsbonus von 16&nbsp;% setzt unter anderem
              eine selbstgenutzte Hauptwohnung und den Austausch einer
              begünstigten alten Heizung voraus.
            </p>
          </InfoBox>

          <InfoBox title="Einkommensbonus" color="amber">
            <p className="text-3xl font-bold text-amber-700">+10–40 %</p>
            <p>
              Die Höhe hängt vom zu versteuernden Haushaltsjahreseinkommen ab.
              Mit mindestens einem kindergeldberechtigten Kind unter 18 steigen
              die Einkommensgrenzen einmalig um 10.000&nbsp;€.
            </p>
          </InfoBox>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <p className="text-sm leading-6 text-slate-700">
            <b>Wichtig:</b> Förderanträge müssen in der Regel vor verbindlicher
            Beauftragung beziehungsweise nach den jeweils gültigen
            Programmbedingungen gestellt werden. Die tatsächliche Förderhöhe
            sollte immer vor Vertragsabschluss geprüft werden.
          </p>
        </div>
      </section>

      {/* WIRTSCHAFTLICHE EINORDNUNG */}
      <section className="mx-4 mt-10 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <p className="text-sm font-semibold text-slate-500">Entscheidungshilfe</p>
        <h2 className="mb-6 mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Wie sollte man die Ergebnisse interpretieren?
        </h2>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6">
            <h3 className="mb-4 text-lg font-semibold text-emerald-900">
              Erdwärme ist besonders interessant, wenn ...
            </h3>
            <ul className="list-disc pl-6 text-gray-800 space-y-2">
              <li>das Gebäude einen langfristig hohen Wärmebedarf hat</li>
              <li>niedrige Betriebskosten wichtiger sind als minimale Investition</li>
              <li>eine alte Gas- oder Ölheizung ersetzt wird</li>
              <li>Förderboni genutzt werden können</li>
              <li>Grundstück und Untergrund für Erdwärme geeignet sind</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">
              Vorsicht ist geboten, wenn ...
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>die Heizlast noch nicht bekannt ist</li>
              <li>die Vorlauftemperaturen sehr hoch sind</li>
              <li>Bohrungen am Standort schwierig oder nicht genehmigungsfähig sind</li>
              <li>nur eine sehr kurze Nutzungsdauer geplant ist</li>
              <li>die Investition ohne Förderung kaum tragbar wäre</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50/70 p-5">
          <p className="text-sm leading-6 text-slate-700">
            <b>Wichtig:</b> Der Vergleich ersetzt keine Heizlastberechnung,
            Fachplanung oder Genehmigungsprüfung. Er zeigt aber, ob sich eine
            nähere Prüfung von Erdwärme wirtschaftlich lohnen kann.
          </p>
        </div>
      </section>

      {/* CTA */}
      <div className="mx-auto mt-10 max-w-6xl px-4 sm:px-0">
        <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-7 text-white shadow-xl sm:p-10">
          <p className="mb-2 text-sm font-semibold text-emerald-300">Nächster Schritt</p>
          <h2 className="mb-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Beispielwerte reichen für Ihr Grundstück nicht aus?
          </h2>

          <p className="max-w-3xl leading-7 text-slate-300">
            Der GeoReport prüft Ihr Grundstück standortbezogen und ordnet ein,
            ob Erdwärme grundsätzlich sinnvoll erscheint – inklusive
            Ersteinschätzung zu Untergrund, Systemwahl, Genehmigung,
            Erschließungskosten und Wirtschaftlichkeit.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/report"
              title="Erdwärme auf dem Grundstück prüfen – GeoReport"
              className="inline-flex items-center justify-center rounded-full bg-red-600 px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-red-500"
            >
              Grundstück individuell prüfen
            </Link>

            <Link
              to="/flache-geothermie"
              className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/15"
            >
              Grundlagen zur flachen Geothermie
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
